/**
 * CustomWidthBlock custom element.
 *
 * Handles:
 *  - Show/hide the number input when a "custom width" variant option is selected.
 *  - Validates and rounds the input value to the nearest 0.5.
 *  - Disables the Add-to-Cart button when the input is required but empty/invalid.
 *  - Updates the shared <quantity-total-display> element (next to qty selector)
 *    with input × qty cm when active.
 *  - Reacts to variant:update and quantity-selector:update theme events.
 */
class CustomWidthBlock extends HTMLElement {
  /** @type {AbortController|null} */
  #listenerController = null;

  /** Tracks the current quantity for the total display. */
  #currentQty = 1;

  connectedCallback() {
    this.#listenerController = new AbortController();
    this.#applyState();
    this.#attachListeners();
  }

  disconnectedCallback() {
    this.#listenerController?.abort();
    this.#listenerController = null;
  }

  // ─── Private helpers ────────────────────────────────────────────────────────

  get #isActive() { return this.dataset.show === 'true'; }
  get #min() { return parseFloat(this.dataset.min) || 0; }
  get #max() { return parseFloat(this.dataset.max) || Infinity; }
  get #formId() { return this.dataset.productFormId; }
  get #propertyName() { return this.dataset.propertyName || 'Gewünschte Breite'; }

  get #input() { return this.querySelector('.custom-width-block__input'); }

  /**
   * Find the <quantity-total-display> element rendered by quantity-selector.liquid
   * within the same shopify section.
   */
  get #qtyTotalDisplay() {
    return this.closest('.shopify-section')?.querySelector('quantity-total-display') ?? null;
  }

  get #submitButton() {
    const form = this.#formId
      ? document.getElementById(this.#formId) ?? document.querySelector(`[id="${this.#formId}"]`)
      : null;
    if (form) return form.querySelector('[type="submit"]');
    return document.querySelector('[data-type="add-to-cart-form"] [type="submit"]');
  }

  /** Round to the nearest 0.5. */
  #roundToHalf(value) {
    return Math.round(value * 2) / 2;
  }

  #isInputValid() {
    if (!this.#isActive) return true;
    const input = this.#input;
    if (!input) return true;
    const raw = input.value.trim();
    if (raw === '') return false;
    const val = parseFloat(raw);
    return !isNaN(val) && val >= this.#min && val <= this.#max;
  }

  #validateAndToggleAtc() {
    const btn = this.#submitButton;
    if (!btn) return;

    if (!this.#isActive) {
      if (btn.dataset.customWidthDisabled) {
        delete btn.dataset.customWidthDisabled;
        // Only re-enable if no other gate (e.g. coating terms) still blocks it.
        if (!btn.dataset.coatingTermsDisabled) btn.removeAttribute('disabled');
      }
      return;
    }

    if (this.#isInputValid()) {
      if (btn.dataset.customWidthDisabled) {
        delete btn.dataset.customWidthDisabled;
        // Only re-enable if no other gate (e.g. coating terms) still blocks it.
        if (!btn.dataset.coatingTermsDisabled) btn.removeAttribute('disabled');
      }
    } else {
      btn.setAttribute('disabled', '');
      btn.dataset.customWidthDisabled = '1';
    }
  }

  /**
   * Update the <quantity-total-display> element.
   * Uses queueMicrotask so this runs after quantity-total-display.js's own
   * variant:update handler (which may hide the element for custom-width variants).
   *
   * @param {number} [qty]
   */
  #updateQtyDisplay(qty = this.#currentQty) {
    queueMicrotask(() => {
      const el = this.#qtyTotalDisplay;
      if (!el) return;

      if (!this.#isActive) {
        // Standard variant — let quantity-total-display.js manage the unit/visibility.
        return;
      }

      const input = this.#input;
      const raw = input ? parseFloat(input.value) : NaN;

      if (isNaN(raw) || raw <= 0) {
        // Custom width empty/invalid — hide the display.
        el.hidden = true;
        return;
      }

      // The custom width becomes the per-unit width so the reverse (total → qty)
      // converter in quantity-total-display.js works for custom-width variants too.
      el.dataset.unit = String(raw);
      el.hidden = false;

      const totalInput = el.querySelector('.quantity-total-display__input');
      if (totalInput) {
        totalInput.value = String(this.#roundToHalf(raw * qty));
      }
    });
  }

  #applyState() {
    const active = this.#isActive;
    this.hidden = !active;

    const input = this.#input;
    if (input) {
      if (active) {
        input.disabled = false;
        input.min = this.dataset.min;
        input.max = this.dataset.max;
        input.name = `properties[${this.#propertyName}]`;
      } else {
        // Disable so the required input is excluded from form constraint
        // validation (form.checkValidity()) when custom width is not needed.
        input.disabled = true;
      }
    }

    this.#validateAndToggleAtc();
    this.#updateQtyDisplay();
  }

  // ─── Event listeners ────────────────────────────────────────────────────────

  #attachListeners() {
    const signal = this.#listenerController.signal;
    const input = this.#input;

    if (input) {
      input.addEventListener('input', () => {
        this.#validateAndToggleAtc();
        this.#updateQtyDisplay();
      }, { signal });

      input.addEventListener('blur', () => {
        const raw = parseFloat(input.value);
        if (!isNaN(raw)) {
          input.value = Math.min(this.#max, Math.max(this.#min, this.#roundToHalf(raw)));
        }
        this.#validateAndToggleAtc();
        this.#updateQtyDisplay();
      }, { signal });
    }

    const scope = this.closest('.shopify-section, dialog') || document;

    scope.addEventListener('variant:update', (event) => {
      this.#handleVariantUpdate(event);
    }, { signal });

    scope.addEventListener('quantity-selector:update', (event) => {
      this.#currentQty = event.detail?.quantity ?? 1;
      this.#updateQtyDisplay(this.#currentQty);
    }, { signal });
  }

  #handleVariantUpdate(event) {
    const html = event.detail?.data?.html;
    if (!html) return;

    const updated = html.querySelector('custom-width-block');
    if (!updated) {
      this.dataset.show = 'false';
      this.dataset.min = '';
      this.dataset.max = '';
    } else {
      this.dataset.show = updated.dataset.show;
      this.dataset.min  = updated.dataset.min;
      this.dataset.max  = updated.dataset.max;

      // Keep the info line (min/max hint) in sync with the newly selected range.
      const updatedInfo = updated.querySelector('.custom-width-block__info');
      const currentInfo = this.querySelector('.custom-width-block__info');
      if (updatedInfo && currentInfo) currentInfo.textContent = updatedInfo.textContent;
    }

    // Clear the input so stale values from a different range aren't submitted.
    const input = this.#input;
    if (input) input.value = '';

    this.#applyState();
    this.#updateQtyDisplay();
  }
}

customElements.define('custom-width-block', CustomWidthBlock);

